import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { SITE } from "@/lib/site";
import { GUIDES } from "@/lib/guides";

export const Route = createFileRoute("/guide/")({
  head: () => {
    const url = `${SITE.url}/guide`;
    return {
      meta: [
        { title: `Local SEO Guides — Map Pack, GBP & AI Search | ${SITE.name}` },
        {
          name: "description",
          content:
            "Evergreen guides on Google Business Profile, the Map Pack, local SEO, and AI search — for agencies, GBP specialists, and local business owners.",
        },
        { property: "og:title", content: `Local SEO Guides | ${SITE.name}` },
        { property: "og:description", content: "Plain-English guides on GBP, the Map Pack, and AI search for local." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Local SEO Guides",
            url,
            isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
            hasPart: GUIDES.map((g) => ({
              "@type": "Article",
              headline: g.title,
              url: `${SITE.url}/guide/${g.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: GuideIndex,
});

function GuideIndex() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li>Guides</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Start here</p>
          <h1 className="headline-display mt-3 text-5xl">Local SEO guides</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Evergreen explainers on Google Business Profile, the Map Pack, local ranking, and the
            AI search systems reshaping how people find local businesses. Written for SEO agencies
            and the operators they serve.
          </p>
        </header>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to="/guide/$slug"
              params={{ slug: g.slug }}
              className="block border border-border p-6 hover:border-foreground transition-colors"
            >
              <p className="eyebrow">{g.eyebrow}</p>
              <h2 className="headline-display mt-2 text-2xl">{g.title}</h2>
              <p className="mt-3 text-sm text-muted-foreground">{g.summary}</p>
              <span className="mt-4 inline-block text-xs font-medium text-accent">Read the guide →</span>
            </Link>
          ))}
        </div>
        <NewsletterCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
